import { useMemo } from "react";

import { makeInactiveOnCondition } from "@/components/pitchCorrection/inactiveOnCondition";
import { keyStore } from "@/components/pitchCorrection/PitchCorrection";
import { handleKnobPitchChangeInternal } from "@/components/pitchCorrection/pitchKnob/handleKnobPitchChangeInternal";
import { pitchDiffsFromSemitoneData } from "@/components/pitchCorrection/pitchKnob/pitchDiffsFromSemitoneData";
import { RotaryDial } from "@/components/pitchCorrection/RotaryDial";
import { useNamedKey } from "@/lib/zustand/useKey";
import { useNamedPitchCorrection } from "@/lib/zustand/usePitchCorrection";
import { Undoable } from "@/types/defs/pitchCorrection/undoable";

export function PitchKnob({
  inactiveCondition,
  onEnd,
  onChange,
  onStart,
  namespace,
  keyNamespace,
  size = 40,
  sensitivity = 0.3,
  disabled = false,
}: {
  inactiveCondition: boolean;
  namespace: string;
  // The key's own namespace, matching the hosting editor's keyNamespace. In the harmonies track editor it differs from `namespace` (the track store): reading the key from the track namespace returned that store's untouched 'No Key' default, so the knob snapped chromatically and its undo snapshot carried 'No Key' — which the editor's undo then wrote into the SHARED project key store, collapsing every track's harmony.
  keyNamespace: string;
  disabled?: boolean;
  onStart?: (v: number) => void;
  onChange?: (v: number) => void;
  onEnd?: (v: number) => void;
  size?: number;
  sensitivity?: number;
}) {
  const usePitchCorrection = useNamedPitchCorrection((state) =>
    state.getNamedStore(namespace)
  );
  const useKey = useNamedKey((state) => state.getNamedStore(keyNamespace));
  const key = useKey((state) => state.key);
  const pitchesDirty = usePitchCorrection((state) => state.pitchesDirty);
  const knobPitchValue = usePitchCorrection((state) => state.pitchLevel);
  const setKnobPitchValue = usePitchCorrection((state) => state.setPitchLevel);
  const setPitches = usePitchCorrection((state) => state.setPitches);

  const setPreviousPitchLevel = usePitchCorrection(
    (state) => state.setPreviousPitchLevel
  );
  const previousPitchLevel = usePitchCorrection(
    (state) => state.previousPitchLevel
  );

  const previousVibratoLevel = usePitchCorrection(
    (state) => state.previousVibratoLevel
  );
  const previousPitchPitches = usePitchCorrection(
    (state) => state.previousPitchPitches
  );
  const setPreviousPitchPitches = usePitchCorrection(
    (state) => state.setPreviousPitchPitches
  );

  const originalPitches = usePitchCorrection((state) => state.originalPitches);
  const unsetPreviousPitchPitches = usePitchCorrection(
    (state) => state.unsetPreviousPitchPitches
  );
  const pitches = usePitchCorrection((state) => state.pitches);
  const circles = usePitchCorrection((state) => state.circles);
  const addToUndoStack_ = usePitchCorrection((state) => state.addToUndoStack);
  const setRedoStack = usePitchCorrection((state) => state.setRedoStack);

  const addToUndoStack = (a: Undoable) => {
    addToUndoStack_(a);
    setRedoStack([]);
  };

  const pitchDiffs = useMemo(
    () =>
      originalPitches.length > 0 && circles.length > 0 ?
        pitchDiffsFromSemitoneData(originalPitches, keyStore([])[key], circles)
      : [],
    [originalPitches.length, circles.length, key]
  );

  const updatePitchesAndUndoStack = (knobValue: number) => {
    const newPitches = handleKnobPitchChangeInternal(
      knobValue,
      previousPitchLevel,
      previousPitchPitches ?? pitches,
      pitchDiffs,
      pitchDiffs,
      pitchesDirty
    );
    addToUndoStack({
      time: new Date(),
      source: "edit",
      value: {
        pitches: pitches.slice(),
        vibratoLevel: previousVibratoLevel,
        pitchLevel: previousPitchLevel,
        key,
      },
    });
    setPitches(newPitches);
  };

  return (
    <RotaryDial
      disabled={disabled}
      value={knobPitchValue}
      setValue={setKnobPitchValue}
      size={size}
      sensitivity={sensitivity}
      onEnd={makeInactiveOnCondition(inactiveCondition, (v) => {
        unsetPreviousPitchPitches();
        setPreviousPitchLevel(v);
        if (onEnd !== undefined) {
          onEnd(v);
        }
      })}
      onStart={makeInactiveOnCondition(inactiveCondition, (v) => {
        setPreviousPitchPitches(pitches);
        if (onStart !== undefined) {
          onStart(v);
        }
      })}
      onChange={makeInactiveOnCondition(inactiveCondition, (n) => {
        if (previousPitchPitches === null) {
          setPreviousPitchPitches(pitches);
        }
        setKnobPitchValue(n);
        if (pitches.length > 0) {
          updatePitchesAndUndoStack(n);
        }
        if (onChange !== undefined) {
          onChange(n);
        }
      })}
    />
  );
}
